import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, dateFmt } from "@/lib/api";
import { KANBAN_COLUMNS } from "@/lib/constants";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { Filter, RefreshCw, Plus, MapPin, Truck, Focus } from "lucide-react";

export default function Dispatch() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/dashboard/operational");
            if (data) setServices(data.services || []);
        } catch (e) {
            console.warn("dispatch fetch failed", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, []);

    const filtered = services.filter((s) => {
        if (!filter) return true;
        const q = filter.toLowerCase();
        return (
            s.folio?.toLowerCase().includes(q) ||
            s.patient_name?.toLowerCase().includes(q) ||
            s.client_name?.toLowerCase().includes(q) ||
            s.service_type_name?.toLowerCase().includes(q)
        );
    });

    const byColumn = KANBAN_COLUMNS.map((col) => ({
        ...col,
        items: filtered.filter((s) => col.statuses.includes(s.status)),
    }));

    return (
        <div className="h-full flex flex-col" data-testid="dispatch-board">
            <div className="flex items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-2 flex-1">
                    <Filter className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                    <input
                        placeholder="Filtrar por folio, paciente, cliente..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="fm-input max-w-md"
                        data-testid="dispatch-filter"
                    />
                </div>
                <button onClick={load} className="fm-btn-outline" data-testid="refresh-btn">
                    <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} /> Actualizar
                </button>
                <Link
                    to="/focus"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 hover:bg-red-600 text-white rounded-sm text-xs font-bold uppercase tracking-widest border border-slate-900 hover:border-red-600 transition-colors"
                    data-testid="dispatch-focus-btn"
                    title="Pantalla completa para cabina"
                >
                    <Focus className="w-3.5 h-3.5" strokeWidth={2} /> Concentración
                </Link>
                <Link to="/services/new" className="fm-btn-accent" data-testid="new-service-btn">
                    <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Nueva Solicitud
                </Link>
            </div>

            {loading ? (
                <div className="text-sm text-slate-500 font-mono">Cargando despacho...</div>
            ) : (
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 overflow-hidden">
                    {byColumn.map((col) => (
                        <div key={col.key} className="flex flex-col bg-white border border-slate-200 rounded-sm overflow-hidden">
                            <div className="px-3 py-2.5 bg-slate-900 text-white flex items-center justify-between">
                                <span className="font-display font-bold text-[11px] uppercase tracking-widest">{col.label}</span>
                                <span className="font-mono text-xs tabular-nums bg-slate-800 px-2 py-0.5 rounded-sm">{col.items.length}</span>
                            </div>
                            <div className="flex-1 overflow-y-auto p-2 space-y-2 bg-slate-50">
                                {col.items.length === 0 ? (
                                    <div className="text-[11px] text-slate-400 text-center py-4 font-mono">Sin servicios</div>
                                ) : (
                                    col.items.map((s) => (
                                        <Link
                                            key={s.id}
                                            to={`/services/${s.id}`}
                                            className="block bg-white border border-slate-200 hover:border-slate-400 hover:shadow-md rounded-sm p-2.5 transition-all"
                                            data-testid={`dispatch-card-${s.folio}`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-mono text-[10px] font-bold text-slate-900">{s.folio}</span>
                                                <UrgencyBadge urgency={s.urgency_level} />
                                            </div>
                                            <div className="text-[13px] font-semibold text-slate-900 truncate">{s.patient_name}</div>
                                            <div className="text-[11px] text-slate-500 truncate mb-1.5">{s.client_name}</div>
                                            <div className="text-[10px] text-slate-600 mb-1.5">
                                                <span className="font-mono">{s.service_type_code}</span> · {s.service_type_name}
                                            </div>
                                            <div className="flex items-center gap-1 text-[10px] text-slate-500 mb-1.5">
                                                <MapPin className="w-2.5 h-2.5" strokeWidth={1.5} />
                                                <span className="truncate">{s.origin_address}</span>
                                            </div>
                                            {s.unit_number && (
                                                <div className="flex items-center gap-1 text-[10px] text-slate-700">
                                                    <Truck className="w-2.5 h-2.5" strokeWidth={1.5} />
                                                    <span className="font-mono font-bold">{s.unit_number}</span>
                                                </div>
                                            )}
                                            <div className="mt-1.5 pt-1.5 border-t border-slate-100 flex items-center justify-between">
                                                <StatusBadge status={s.status} />
                                                <span className="text-[9px] text-slate-400 font-mono">{dateFmt(s.created_at)}</span>
                                            </div>
                                        </Link>
                                    ))
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
